import { createApi, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { AxiosRequestConfig, AxiosError } from 'axios';
import axiosInstance from 'api/axios';
import { Bonus } from 'types/bonus';

const axiosBaseQuery =
  (): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
    },
    unknown,
    unknown
  > =>
  async ({ url, method = 'GET', data, params }) => {
    try {
      const result = await axiosInstance({ url, method, data, params });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const bonusesApi = createApi({
  reducerPath: 'bonusesApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Bonus'],
  endpoints: (builder) => ({
    getBonuses: builder.query<Bonus[], void>({
      query: () => {
        const siteId = process.env.REACT_APP_TENANT_ID || '1';
        return { url: `/client/levels/bonuses?siteId=${siteId}`, method: 'GET' };
      },
      transformResponse: (response: any) => {
        const bonusDefinitions = response?.data?.bonusDefinitions || [];
        const claims = response?.data?.claims || [];

        return bonusDefinitions.map((b: any, index: number) => {
          const apiClaim = claims.find((c: any) => c.bonusType === b.bonusType);
          const claimable = apiClaim?.claimable || b.claimable || [];

          return {
            id: b.bonusType || String(index),
            siteBonusId: b.bonusType || '',
            title: b.title || '',
            icon: b.imageUrl || '',
            startTier: b.startTier || '',
            claimable,
            pendingUsage: apiClaim ? { bonusUsageId: b.bonusType } : null,
            locked: b.enabled !== undefined ? !b.enabled : false,
            unlocksAt: b.unlocksAt || null,
            sortOrder: b.sortOrder || 0,
            info: {
              bonusIncludes: b.description || '',
              expiresAt: '',
              title: b.title || '',
              bonusDetails: b.description || '',
              imageUrl: b.imageUrl || null,
            },
          };
        });
      },
      providesTags: ['Bonus'],
    }),
    getFreeSpinGames: builder.query<any[], void>({
      query: () => ({ url: `/client/levels/bonuses/free-spin/games`, method: 'GET' }),
      transformResponse: (response: any) => response?.data || [],
    }),
    getFreeSpinBetLevels: builder.query<any, number>({
      query: (gameId) => ({
        url: `/client/levels/bonuses/free-spin/bet-levels?gameId=${gameId}`,
        method: 'GET',
      }),
      transformResponse: (response: any) => response?.data || {},
    }),
  }),
});

export const { useGetBonusesQuery, useGetFreeSpinGamesQuery, useGetFreeSpinBetLevelsQuery } =
  bonusesApi;

export const updateBonusLocal = (data: any) =>
  bonusesApi.util.updateQueryData('getBonuses', undefined, (draft) => {
    if (!data || data.bonusType == null) return;

    const existingIndex = draft.findIndex((eb) => eb.id === data.bonusType);
    const existing = existingIndex !== -1 ? draft[existingIndex] : null;

    const mappedBonus: Bonus = {
      id: data.bonusType,
      siteBonusId: data.bonusType,
      title: data.title !== undefined ? data.title : existing?.title || '',
      icon: data.imageUrl !== undefined ? data.imageUrl : existing?.icon || '',
      startTier: data.startTier !== undefined ? data.startTier : existing?.startTier || '',
      claimable: existing?.claimable || data.claimable || [],
      pendingUsage: existing?.pendingUsage || null,
      locked: data.enabled !== undefined ? !data.enabled : (existing?.locked ?? false),
      unlocksAt: data.unlocksAt !== undefined ? data.unlocksAt : existing?.unlocksAt || null,
      sortOrder: data.sortOrder !== undefined ? data.sortOrder : existing?.sortOrder || 0,
      info: {
        bonusIncludes:
          data.description !== undefined ? data.description : existing?.info?.bonusIncludes || '',
        expiresAt: existing?.info?.expiresAt || '',
        title: data.title !== undefined ? data.title : existing?.info?.title || '',
        bonusDetails:
          data.description !== undefined ? data.description : existing?.info?.bonusDetails || '',
        imageUrl: data.imageUrl !== undefined ? data.imageUrl : existing?.info?.imageUrl || null,
      },
    };

    if (existingIndex !== -1) {
      draft[existingIndex] = mappedBonus;
    } else {
      draft.push(mappedBonus);
    }
    draft.sort((x, y) => (x.sortOrder || 0) - (y.sortOrder || 0));
  });

export const reorderBonusesLocal = (newDefinitions: any[]) =>
  bonusesApi.util.updateQueryData('getBonuses', undefined, (draft) => {
    if (!Array.isArray(newDefinitions)) return;

    const updatedDraft = newDefinitions.map((b: any, index: number) => {
      const existing = draft.find((eb) => eb.id === b.bonusType);
      return {
        id: b.bonusType || String(index),
        siteBonusId: b.bonusType || '',
        title: b.title !== undefined ? b.title : existing?.title || '',
        icon: b.imageUrl !== undefined ? b.imageUrl : existing?.icon || '',
        startTier: b.startTier !== undefined ? b.startTier : existing?.startTier || '',
        claimable: existing?.claimable || b.claimable || [],
        pendingUsage: existing?.pendingUsage || null,
        locked: b.enabled !== undefined ? !b.enabled : (existing?.locked ?? false),
        unlocksAt: b.unlocksAt !== undefined ? b.unlocksAt : existing?.unlocksAt || null,
        sortOrder: b.sortOrder !== undefined ? b.sortOrder : existing?.sortOrder || index,
        info: {
          bonusIncludes:
            b.description !== undefined ? b.description : existing?.info?.bonusIncludes || '',
          expiresAt: existing?.info?.expiresAt || '',
          title: b.title !== undefined ? b.title : existing?.info?.title || '',
          bonusDetails:
            b.description !== undefined ? b.description : existing?.info?.bonusDetails || '',
          imageUrl: b.imageUrl !== undefined ? b.imageUrl : existing?.info?.imageUrl || null,
        },
      };
    });

    // Replace draft contents
    draft.splice(0, draft.length, ...updatedDraft);
    draft.sort((x, y) => (x.sortOrder || 0) - (y.sortOrder || 0));
  });

export const claimBonusLocal = (bonusId: string) =>
  bonusesApi.util.updateQueryData('getBonuses', undefined, (draft) => {
    const bonus = draft.find((b) => b.id === bonusId);
    if (bonus) {
      bonus.claimable = [];
    }
  });
